// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useState } from "react";
import {
  Button,
  Box,
  Divider,
  Stack,
  Typography,
  Chip,
  Card,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { generatePath, useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import ManageSdkTabs from "../components/ManageSdkTabs";
import type { Application, BuildInfo } from "../types/projects";
import hourglassEmptyUrl from "../../assets/hourglass_empty.svg";
import SdkApkDownloadDialog from "../components/SdkApkDownloadDialog";
import { formatDate } from "../utils/dates";
import {
  useDownloadSdkMutation,
  useLazyListApplicationBuildsQuery,
  useListApplicationsQuery,
} from "../features/data/projectApiSlice";
import { DASHBOARD_PATH } from "../utils/constants";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";

const ManageSdk = () => {
  const { t } = useTranslation("ManageSdk");
  const location = useLocation();
  const { projectName, projectUid, testUid } = location.state || {};
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const navigate = useNavigate();

  const [selectedApp, setSelectedApp] = useState<Application>();
  const [selectedBuild, setSelectedBuild] = useState<BuildInfo>();
  const [isDownloadingSdk, setIsDownloadingSdk] = useState(false);

  const { data: listApplicationsData } = useListApplicationsQuery(
    { projectUid },
    { skip: !projectUid }
  );
  const [getApplicationBuilds, { data: listAppBuildsData }] = useLazyListApplicationBuildsQuery();
  const [downloadSdk] = useDownloadSdkMutation();

  const [isDownloadDialog, setIsDownloadDialog] = useState(false);

  const handleDownloadDialogOpen = (build: BuildInfo) => {
    setSelectedBuild(build);
    setIsDownloadDialog(true);
  };
  const handleDownloadDialogClose = () => setIsDownloadDialog(false);
  const handleAppSelect = useCallback(
    (app: Application) => {
      setSelectedApp(app);
      getApplicationBuilds({ applicationUid: app.application_uid });
    },
    [getApplicationBuilds]
  );
  const handleDownloadSdk = useCallback(() => {
    if (selectedBuild?.app_build_uid) {
      setIsDownloadingSdk(true);
      downloadSdk({ buildUid: selectedBuild.app_build_uid })
        .then(({ data }) => {
          const sdkUrl = data?.download_urls?.sdk_zip;
          if (sdkUrl) {
            const link = document.createElement("a");
            link.href = sdkUrl;
            link.download = "";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
        .finally(() => {
          setIsDownloadingSdk(false);
          handleDownloadDialogClose();
        });
    }
  }, [downloadSdk, selectedBuild]);
  const handleProjectNameClick = () => {
    navigate(generatePath(DASHBOARD_PATH, { orgUid: currentOrganizationId, projectUid, testUid }));
  };

  return (
    <Box sx={styles.container}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        <Link
          underline="hover"
          color="textSecondary"
          sx={styles.projectName}
          onClick={handleProjectNameClick}
        >
          {projectName}
        </Link>
        <Link underline="none" color="primary">
          {t("common:generateSdk")}
        </Link>
      </Breadcrumbs>
      <Box sx={styles.headerContainer} maxWidth="lg">
        <Typography variant="h6" fontWeight={400}>
          {projectName}
        </Typography>
        <Typography variant="h6" fontSize={40}>
          {t("pageTitle")}
        </Typography>
      </Box>
      <Box sx={styles.mainContent} maxWidth="lg">
        <ManageSdkTabs
          previousApps={listApplicationsData?.applications ?? []}
          onAppSelect={handleAppSelect}
        />
        <Box sx={styles.panelContainer}>
          {listApplicationsData?.applications?.length ? (
            <Box sx={styles.previousDownloadsContainer}>
              <Stack sx={styles.panelHeader}>
                <Stack>
                  <Typography variant="body1bold" sx={styles.previousDownloadsTitle}>
                    {t("previousDownloadsTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("previousDownloadsSubtitle")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack>
                {listAppBuildsData?.app_builds?.length ? (
                  listAppBuildsData.app_builds.map(item => (
                    <Box key={item.app_build_uid}>
                      <Card sx={styles.downloadCard}>
                        <Box sx={styles.cardTextContainer}>
                          {(item?.build_status?.state === "FAILURE" ||
                            selectedApp?.latest_successful_build?.build_uid ===
                              item.app_build_uid) && (
                            <Chip
                              label={
                                item?.build_status?.state === "FAILURE" ? t("failed") : t("latest")
                              }
                              size="small"
                              sx={{
                                ...styles.latestChip,
                                background:
                                  item?.build_status?.state === "FAILURE"
                                    ? "rgba(227, 31, 31, 0.2)"
                                    : "rgba(33, 150, 83, 0.15)",
                              }}
                            />
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {t("buildVersion", { version: item.build_version })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedApp?.android_app_id}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {item.build_status?.end_timestamp &&
                              formatDate(item.build_status?.end_timestamp)}
                          </Typography>
                          {selectedBuild?.app_build_uid === item.app_build_uid &&
                          isDownloadingSdk ? (
                            <Typography variant="body2bold" color="common.black">
                              {t("downloadingSdk")}
                            </Typography>
                          ) : (
                            item?.build_status?.state !== "FAILURE" && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={item?.build_status.state !== "SUCCESS"}
                                onClick={() => handleDownloadDialogOpen(item)}
                                sx={styles.downloadBtn}
                              >
                                {t("common:download")}
                              </Button>
                            )
                          )}
                        </Stack>
                      </Card>
                      <Divider />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" px={2}>
                    {t("noPreviousDownloads")}
                  </Typography>
                )}
              </Stack>
            </Box>
          ) : (
            <Box sx={styles.selectPreviousApp}>
              <Box
                component="img"
                src={hourglassEmptyUrl}
                sx={styles.hourglassEmptyImg}
                alt="Hourglass empty"
              />
              <Typography variant="h6" fontSize={24}>
                {t("selectPreviousAppTitle")}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t("selectPreviousAppSubtitle")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <SdkApkDownloadDialog
        open={isDownloadDialog}
        onClose={handleDownloadDialogClose}
        onDownloadSdk={handleDownloadSdk}
      />
    </Box>
  );
};

export default ManageSdk;

const styles = createStyleSheet({
  container: {
    p: 2,
    px: 3,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    background: theme.palette.grey[100],
    height: "100%",
    overflowY: "auto",
  },
  headerContainer: {
    width: "100%",
    alignSelf: "center",
  },
  mainContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "start",
    gap: 4,
    width: "100%",
    alignSelf: "center",
  },
  panelContainer: {
    flex: 1,
  },
  panelHeader: {
    p: 2,
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "flex-start",
    gap: 1,
  },
  downloadCard: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    p: 2,
    boxShadow: "none",
    background: "transparent",
  },
  previousDownloadsContainer: {
    border: `1px solid ${theme.palette.grey[400]}`,
    padding: theme.spacing(5),
  },
  selectPreviousApp: { p: theme.spacing(5) },
  hourglassEmptyImg: { height: theme.spacing(3), objectFit: "contain", mb: 1 },
  previousDownloadsTitle: { mb: 2 },
  latestChip: {
    color: theme.palette.common.black,
    borderRadius: "4px",
    py: 1,
    mb: 0.5,
  },
  downloadBtn: {
    borderColor: theme.palette.action.disabled,
    color: theme.palette.common.black,
  },
  cardTextContainer: { flex: 1 },
  projectName: { cursor: "pointer" },
});
